import React, { useEffect, useState } from 'react';
import { Modal, Form, Select, Button, Input, DatePicker } from 'antd';
import { useDispatch } from 'react-redux';
import { getAllProjects } from 'pages/Tasks/redux/slice';
import moment from 'moment';
import {
  createWeeklyPlansAsync,
  fetchWeeklyPlans,
  updateWeeklyPlansAsync
} from '../redux/slice';
import styles from '../../Dashboard/index.module.less';

const { Option } = Select;
const { RangePicker } = DatePicker;

const AddWeeklyTask = ({
  visible,
  onClose,
  mode = 'add',
  initialData = null,
  initialWeekMonday = null,
  executiveMembers = []
}) => {
  const [form] = Form.useForm();
  const [projects, setProjects] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedWeekMonday, setSelectedWeekMonday] = useState(null);

  const isOpen = visible;
  const dispatch = useDispatch();

  useEffect(() => {
    if (isOpen) {
      dispatch(getAllProjects()).then((res) => {
        if (res?.payload?.success) {
          setProjects(res.payload.data || []);
        }
      });
    }
  }, [dispatch, isOpen]);

  // Get unique clients from projects
  const uniqueClients = Array.from(
    new Map(
      projects.map((proj) => [
        proj.client.id,
        { id: proj.client.id, name: proj.client.client_name }
      ])
    ).values()
  );

  const filteredProjects = selectedClient
    ? projects.filter((proj) => proj.client.id === selectedClient)
    : [];

  const getWeekRangeFromMonday = (monday) => {
    const start = monday.clone();
    const end = monday.clone().add(4, 'days'); // Friday
    return { start, end };
  };

  const disabledDate = (current) => {
    if (!current) return false;

    const day = current.day(); // 0 = Sun, 6 = Sat
    const isWeekend = day === 0 || day === 6;

    const startOfCurrentWeek = moment().startOf('isoWeek');

    // Disable weekends & past weeks
    return isWeekend || current.isBefore(startOfCurrentWeek, 'day');
  };

  const getCurrentWeekRange = () => {
    const monday = initialWeekMonday
      ? moment(initialWeekMonday).clone().startOf('isoWeek')
      : moment().startOf('isoWeek');
    const friday = moment(monday).add(4, 'days');

    return {
      start_date: monday.format('YYYY-MM-DD'),
      end_date: friday.format('YYYY-MM-DD')
    };
  };

  useEffect(() => {
    if (mode === 'add' && isOpen) {
      // Use the week from WeeklyList if passed, otherwise current week
      const monday = initialWeekMonday
        ? moment(initialWeekMonday).clone().startOf('isoWeek')
        : moment().startOf('isoWeek');
      setSelectedWeekMonday(monday);
    }
  }, [mode, isOpen, initialWeekMonday]);

  useEffect(() => {
    if (mode === 'edit' && initialData) {
      const monday = moment(initialData.start_date).startOf('isoWeek');
      setSelectedWeekMonday(monday);

      setSelectedClient(initialData.client_id);

      form.setFieldsValue({
        taskName: initialData.task_name,
        type: initialData.type,
        client: initialData.client_id,
        project: initialData.project_id,
        ...(initialData.executive_id != null && {
          executiveMember: initialData.executive_id
        })
      });
    }
  }, [mode, initialData, form]);

  const handleFinish = (values) => {
    const { start, end } = getWeekRangeFromMonday(selectedWeekMonday);
    const payload = {
      task_name: values.taskName,
      type: values.type,
      client_id: values.client,
      project_id: values.project,
      start_date: start.format('YYYY-MM-DD'),
      end_date: end.format('YYYY-MM-DD'),
      ...(values.executiveMember && {
        executive_id: values.executiveMember
      })
    };

    // ADD id only in edit mode
    if (mode === 'edit') {
      payload.id = initialData.id;
    }

    const action =
      mode === 'edit'
        ? updateWeeklyPlansAsync(payload) // <-- edit API
        : createWeeklyPlansAsync(payload); // <-- add API
    dispatch(action).then((res) => {
      if (res.meta.requestStatus === 'fulfilled') {
        const { start_date, end_date } = getCurrentWeekRange();

        dispatch(
          fetchWeeklyPlans({
            start_date,
            end_date,
            page: 1,
            per_page: 10
          })
        );

        form.resetFields();
        onClose();
      }
    });
  };

  const handleCancel = () => {
    form.resetFields();
    onClose();
  };

  return (
    <Modal
      visible={visible}
      onCancel={handleCancel}
      closable={false}
      footer={null}
      centered
      destroyOnClose
      width={450}
    >
      <Form form={form} layout="vertical" onFinish={handleFinish}>
        <h3 className="font-bold mb-4">
          {mode === 'edit' ? 'Edit Weekly Task' : 'Assign Weekly Task'}
        </h3>

        <Form.Item
          label="Task name"
          name="taskName"
          rules={[{ required: true, message: 'Please enter task name' }]}
          className={styles.customLable}
          style={{ color: '#595959' }}
        >
          <Input
            placeholder="Enter the task name"
            style={{ marginTop: '-5px' }}
          />
        </Form.Item>

        <Form.Item
          label="Type"
          name="type"
          rules={[{ required: true, message: 'Please select task type' }]}
          className={styles.customLable}
          style={{ color: '#595959' }}
        >
          <Select placeholder="Select Type" style={{ marginTop: '-5px' }}>
            <Option value="Business Development">Business Development</Option>
            <Option value="Operations">Operations</Option>
          </Select>
        </Form.Item>

        {/* Executive Member */}
        <Form.Item
          label="Select executive member"
          name="executiveMember"
          className={styles.customLable}
          style={{ color: '#595959' }}
          rules={[
            { required: true, message: 'Please select executive member' }
          ]}
        >
          <Select
            placeholder="Select executive member"
            style={{ marginTop: '-5px' }}
            showSearch
            optionFilterProp="children"
            allowClear
          >
            {executiveMembers.map((member) => (
              <Option key={member.id} value={member.id}>
                {member.first_name} {member.last_name}
              </Option>
            ))}
          </Select>
        </Form.Item>

        {/* Client */}
        <Form.Item
          label="Client"
          name="client"
          rules={[{ required: true, message: 'Please select a client' }]}
          className={styles.customLable}
          style={{ color: '#595959' }}
        >
          <Select
            placeholder="Select Client"
            style={{ marginTop: '-5px' }}
            showSearch
            optionFilterProp="children"
            onChange={(val) => {
              setSelectedClient(val);
              form.setFieldsValue({ project: undefined });
            }}
          >
            {uniqueClients.map((client) => (
              <Option key={client.id} value={client.id}>
                {client.name}
              </Option>
            ))}
          </Select>
        </Form.Item>

        {/* Project */}
        <Form.Item
          label="Project"
          name="project"
          rules={[{ required: true, message: 'Please select a project' }]}
          className={styles.customLable}
          style={{ color: '#595959' }}
        >
          <Select
            placeholder="Select Project"
            style={{ marginTop: '-5px' }}
            showSearch
            optionFilterProp="children"
            disabled={!selectedClient}
          >
            {filteredProjects.map((project) => (
              <Option key={project.id} value={project.id}>
                {project.project}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          label="Select the week range"
          className={styles.customLable}
          style={{ color: '#595959' }}
        >
          <DatePicker
            picker="week"
            allowClear={false}
            value={selectedWeekMonday}
            disabledDate={disabledDate}
            style={{ width: '100%', marginTop: '-5px' }}
            onChange={(date) => {
              if (!date) return;
              setSelectedWeekMonday(moment(date).startOf('isoWeek'));
            }}
            format={(value) => {
              const monday = value.clone().startOf('isoWeek');
              const friday = monday.clone().add(4, 'days');
              return `${monday.format('DD MMM')} - ${friday.format('DD MMM')}`;
            }}
          />
        </Form.Item>

        {/* Actions */}
        <div className="flex justify-center gap-3 mt-4">
          <Button
            onClick={handleCancel}
            className="font-bold border border-blue-500 text-blue-500"
          >
            Cancel
          </Button>
          <Button type="primary" htmlType="submit">
            {mode === 'edit' ? 'Update' : 'Create'}
          </Button>
        </div>
      </Form>
    </Modal>
  );
};

export default AddWeeklyTask;
